import { useEffect, useRef, useState } from 'react'
import { Mic, Square, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import TooltipOverlay from '../TooltipOverlay'
import clsx from 'clsx'
import { toast } from 'sonner'
import {
  AudioConfig,
  CancellationReason,
  ResultReason,
  SpeechConfig,
  SpeechRecognizer,
} from 'microsoft-cognitiveservices-speech-sdk'
import { useAppStore } from '@/store'
import { CHAT_SEND_BUTTON_ID } from '.'
import AudioWaveEffect from '../AudioWaveEffect'
import { useTranslation } from '@/i18n'

const BASE_AUDIO_BTN_CLASS =
  'rounded-full w-[40px] h-[40px] p-0 hover:border-orange-500 hover:bg-orange-500 hover:text-white transition-colors'

const STOP_AUDIO_CLASS =
  'w-[40px] h-[40px] flex items-center justify-center rounded-full text-white bg-red-500 hover:bg-red-600 border-red-500 hover:border-red-600 transition-all'

// Azure speech is only usable when both env vars are present AND real. In local
// dev they're undefined; in a deployed stack that didn't set the corresponding
// INTAI_ENV_* var, docker-fix-env.sh leaves the literal `INTAI_ENV_…` placeholder
// in the bundle — treat that as unconfigured too so we fall back to Web Speech
// instead of calling Azure with a bogus key.
const isAzureSpeechConfigured = (): boolean => {
  const key = import.meta.env.VITE_AZURE_CONFIG_SPEECH_KEY
  const region = import.meta.env.VITE_AZURE_CONFIG_SPEECH_REGION
  return (
    !!key &&
    !!region &&
    !key.startsWith('INTAI_ENV_') &&
    !region.startsWith('INTAI_ENV_')
  )
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition
    webkitSpeechRecognition: new () => SpeechRecognition
  }
}

// Add configuration types
interface AzureConfig {
  subscriptionKey: string
  region: string
}

interface ChatVoiceProps {
  onTranscript: (transcript: string) => Promise<void>
  handleSendMessage: () => Promise<void>
  isRecording: boolean
  setIsRecording: (isRecording: boolean) => void
  useAzure?: boolean
  azureConfig?: AzureConfig
  onAudioStop?: () => void
  borderless?: boolean
}

const ChatVoice: React.FC<ChatVoiceProps> = ({
  onTranscript,
  handleSendMessage,
  isRecording,
  setIsRecording,
  useAzure,
  azureConfig,
  onAudioStop,
  borderless,
}) => {
  const { t } = useTranslation()
  const { audioOutEnabled, isAISpeaking } = useAppStore()
  const [isPreparingRecording, setIsPreparingRecording] = useState(false)
  const [waveEffectVisible, setWaveEffectVisible] = useState(true)
  const webRecognitionRef = useRef<SpeechRecognition | null>(null)
  const azureRecognizerRef = useRef<SpeechRecognizer | null>(null)
  const speakingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)

  const setupWebSpeechRecognition = () => {
    const SpeechRecognitionConstructor =
      window.SpeechRecognition || window.webkitSpeechRecognition
    // No-op when the browser lacks the Web Speech API, so this is safe to call
    // unconditionally (including as the Azure fallback path).
    if (!SpeechRecognitionConstructor) return
    webRecognitionRef.current = new SpeechRecognitionConstructor()
    webRecognitionRef.current.continuous = true
    webRecognitionRef.current.interimResults = true
    webRecognitionRef.current.lang = 'en-US'

    webRecognitionRef.current.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0])
        .map((result) => result.transcript)
        .join('')

      // Check if this is a final result
      const isFinal = event.results[event.results.length - 1].isFinal

      if (isFinal) {
        handleTranscriptSubmission(transcript.trim())
      }
    }

    webRecognitionRef.current.onerror = (event) => {
      toast.error(
        `${t('chat:voice.errors.speech_recognition_error')}: ${event.error}`
      )
      setIsRecording(false)
      setIsPreparingRecording(false)
    }
  }

  // Update the transcript submission handler
  const handleTranscriptSubmission = async (transcript: string) => {
    if (transcript.trim()) {
      // First set the transcript in the input and wait for stop/audio cleanup
      await onTranscript(transcript.trim())

      // Use a small timeout to ensure the input is updated before sending
      setTimeout(() => {
        // Now trigger the send button click
        const sendButton = document.getElementById(CHAT_SEND_BUTTON_ID)

        if (sendButton) {
          sendButton.click()
          // Ensure to stop recording after sending a message to avoid multiple submissions
          stopRecording()
        } else {
          // Fallback to handleSendMessage if button not found
          handleSendMessage()
        }
      }, 100)
    }
  }

  const setupAudioAnalysis = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream

      audioContextRef.current = new AudioContext()
      analyserRef.current = audioContextRef.current.createAnalyser()
      const source = audioContextRef.current.createMediaStreamSource(stream)
      source.connect(analyserRef.current)

      // Start monitoring audio levels
      monitorAudioLevel()
    } catch (error) {
      console.error('Error setting up audio analysis:', error)
    }
  }

  const monitorAudioLevel = () => {
    if (!analyserRef.current) return

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)

    const checkLevel = () => {
      if (!analyserRef.current) return

      analyserRef.current.getByteFrequencyData(dataArray)
      requestAnimationFrame(checkLevel)
    }

    checkLevel()
  }

  const startAzureRecognition = async () => {
    // Credentials come from the azureConfig prop when a caller provides one,
    // otherwise from the build-time env vars. isAzureSpeechConfigured() has already
    // validated the env vars before we reach here, so there's no separate prop
    // presence-check: the earlier `if (!azureConfig)` guard wrongly rejected the
    // env-only callers (entry card, docked chat), toasting "config missing" and
    // returning while startRecording() still flipped the button to "recording".
    const subscriptionKey =
      azureConfig?.subscriptionKey || import.meta.env.VITE_AZURE_CONFIG_SPEECH_KEY
    const region =
      azureConfig?.region || import.meta.env.VITE_AZURE_CONFIG_SPEECH_REGION

    await setupAudioAnalysis()

    const speechConfig = SpeechConfig.fromSubscription(subscriptionKey, region)
    speechConfig.speechRecognitionLanguage = 'en-US'

    const audioConfig = AudioConfig.fromDefaultMicrophoneInput()
    azureRecognizerRef.current = new SpeechRecognizer(speechConfig, audioConfig)

    azureRecognizerRef.current.recognized = (_sender, event) => {
      if (event.result.reason === ResultReason.RecognizedSpeech) {
        const transcript = event.result.text.trim()
        if (transcript) {
          handleTranscriptSubmission(transcript)
        }
      }
    }

    azureRecognizerRef.current.canceled = (_sender, event) => {
      if (event.reason === CancellationReason.Error) {
        toast.error(
          `${t('chat:voice.errors.recognition_canceled')}: ${
            event.errorDetails
          }`
        )
      }
      stopRecording()
    }

    await azureRecognizerRef.current.startContinuousRecognitionAsync()
    setIsRecording(true)
  }

  const startRecording = async () => {
    try {
      setIsPreparingRecording(true)
      await navigator.mediaDevices.getUserMedia({ audio: true })

      if (useAzure && isAzureSpeechConfigured()) {
        try {
          await startAzureRecognition()
        } catch (error) {
          console.warn(
            'Failed to start Azure recognition, falling back to web recognition:',
            error
          )
          if (webRecognitionRef.current) {
            webRecognitionRef.current.start()
          }
        }
      } else if (webRecognitionRef.current) {
        // Azure not configured (or not requested): use the browser's Web Speech API.
        webRecognitionRef.current.start()
      } else {
        // Neither Azure nor the Web Speech API is available — surface it instead of
        // silently flipping the button to "recording" with no engine running.
        toast.error(
          t('chat:voice.errors.not_supported', {
            defaultValue: 'Voice input is not supported in this browser',
          })
        )
        return
      }

      setIsRecording(true)
    } catch (error) {
      toast.error(
        `${t('chat:voice.errors.error_starting_recording')}: ${error}`
      )
    } finally {
      setIsPreparingRecording(false)
    }
  }

  const stopRecording = () => {
    // Clean up audio analysis
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    analyserRef.current = null

    if (useAzure && azureRecognizerRef.current) {
      azureRecognizerRef.current.stopContinuousRecognitionAsync()
      azureRecognizerRef.current = null
    } else if (webRecognitionRef.current) {
      webRecognitionRef.current.stop()
    }

    setIsRecording(false)
  }

  const handleRecordingToggle = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  useEffect(() => {
    // Always set up the Web Speech recognizer (no-ops when unsupported) so it can
    // serve as the fallback when Azure speech isn't configured — e.g. local dev,
    // where VITE_AZURE_CONFIG_SPEECH_KEY is unset — or fails at runtime. Azure
    // still takes precedence in startRecording() when it is configured.
    setupWebSpeechRecognition()
    return () => stopRecording()
  }, [useAzure])

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (speakingTimeoutRef.current) {
        clearTimeout(speakingTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <div className="flex gap-3 z-10">
        <TooltipOverlay message={t('chat:voice.record_message')}>
          <Button
            data-testid="record-button"
            onClick={handleRecordingToggle}
            variant="outline"
            disabled={isPreparingRecording}
            className={clsx(
              BASE_AUDIO_BTN_CLASS,
              isRecording && 'bg-orange-500 hover:bg-orange-600',
              borderless && 'border-none shadow-none w-[32px] h-[32px]'
            )}
          >
            {isPreparingRecording ? (
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            ) : isRecording ? (
              <Square className="w-5 h-5 text-white" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </Button>
        </TooltipOverlay>

        {isAISpeaking && audioOutEnabled && (
          <TooltipOverlay message={t('chat:voice.stop_response')}>
            <Button
              variant="outline"
              data-testid="stop-audio-button"
              onClick={onAudioStop}
              onMouseOver={() => setWaveEffectVisible(false)}
              onMouseOut={() => setWaveEffectVisible(true)}
              className={STOP_AUDIO_CLASS}
            >
              {waveEffectVisible ? (
                <AudioWaveEffect className=" fill-white stroke-white" />
              ) : (
                <Square className="w-5 h-5 text-white" />
              )}
            </Button>
          </TooltipOverlay>
        )}
      </div>
    </>
  )
}

export default ChatVoice
